import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

/**
 * Members report viewer (v8.6, route /members/library/:slug).
 *
 * Full-screen view of a single report. The iframe takes the entire
 * available viewport (minus the site header and a slim back-button
 * row), so on phones the report renders at full readable width.
 *
 * Auth flow:
 *   - On mount we call /api/members-fetch-report (no slug = manifest)
 *     just to validate the session cookie and look up the report's
 *     human title for display. If 401, bounce to /members.
 *   - The iframe then loads /api/members-fetch-report?slug=... which
 *     does its own server-side cookie check before returning HTML.
 *
 * The manifest fetch is cheap (one tiny JSON blob) and means we can
 * show "Report not found" up front rather than letting the iframe
 * display a JSON error blob inside the frame.
 */
export default function MembersReport() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/members-fetch-report', {
          credentials: 'same-origin',
        });
        if (cancelled) return;
        if (res.status === 401) {
          window.location.replace('/members?error=session_required');
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', message: `Server error (${res.status})` });
          return;
        }
        const data = await res.json();
        const reports = Array.isArray(data.reports) ? data.reports : [];
        const found = reports.find((r) => r.slug === slug);
        if (!found) {
          setState({ status: 'not_found' });
          return;
        }
        setState({ status: 'ready', report: found });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', message: err.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Back-button handler: if the user has previous history within the
  // SPA, prefer navigate(-1) so the scroll position on the library
  // page is preserved. Otherwise fall through to an explicit
  // navigation. window.history.length > 1 is the heuristic.
  const handleBack = (e) => {
    e.preventDefault();
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/members/library');
    }
  };

  if (state.status === 'loading') {
    return (
      <div style={loadingWrapStyle}>
        <p style={loadingTextStyle}>Loading report…</p>
      </div>
    );
  }

  if (state.status === 'not_found') {
    return (
      <div style={loadingWrapStyle}>
        <p style={{ ...loadingTextStyle, color: '#ffb8b8' }}>
          That report wasn't found.
        </p>
        <Link
          to="/members/library"
          style={{ color: '#4a9eff', marginTop: '1rem', textDecoration: 'none' }}
        >
          ← Back to library
        </Link>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={loadingWrapStyle}>
        <p style={{ ...loadingTextStyle, color: '#ffb8b8' }}>
          {state.message || 'Something went wrong.'}
        </p>
        <Link
          to="/members/library"
          style={{ color: '#4a9eff', marginTop: '1rem', textDecoration: 'none' }}
        >
          ← Back to library
        </Link>
      </div>
    );
  }

  const { report } = state;

  return (
    <div style={pageStyle}>
      {/* Slim back-button row. Keeps the title visible so the user
          always knows which report they're reading. */}
      <div style={topbarStyle}>
        <a href="/members/library" onClick={handleBack} style={backLinkStyle}>
          <span style={backArrowStyle} aria-hidden="true">←</span>
          <span style={backLabelStyle}>Back to library</span>
        </a>
        <div style={topbarTitleStyle} title={report.title}>
          {report.title}
        </div>
      </div>

      <iframe
        title={`Report ${report.slug}`}
        src={`/api/members-fetch-report?slug=${encodeURIComponent(report.slug)}`}
        style={iframeStyle}
      />
    </div>
  );
}

/* ---------- styles ---------- */

// The site header is ~80px tall (set in Layout.jsx). We use the
// remaining viewport for the report so the iframe fills the screen
// minus the site nav and our slim back-row. The back-row is fixed at
// 56px on phones; using 56px in the math keeps the layout precise.
const TOPBAR_HEIGHT = 56;
const SITE_HEADER_OFFSET = 80;

const pageStyle = {
  // No outer padding — the iframe owns the whole viewport.
  minHeight: `calc(100vh - ${SITE_HEADER_OFFSET}px)`,
  display: 'flex',
  flexDirection: 'column',
  background: '#0a1628',
};
const topbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  height: `${TOPBAR_HEIGHT}px`,
  padding: '0 1rem',
  background: 'rgba(13, 33, 55, 0.95)',
  borderBottom: '1px solid rgba(74, 158, 255, 0.15)',
  // Keep visible while iframe scrolls.
  position: 'sticky',
  top: 0,
  zIndex: 10,
};
const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#4a9eff',
  textDecoration: 'none',
  fontSize: '0.95rem',
  fontWeight: 600,
  padding: '0.5rem 0.75rem 0.5rem 0',
  // Generous tap target for phones.
  minHeight: '44px',
  flexShrink: 0,
};
const backArrowStyle = {
  fontSize: '1.25rem',
  lineHeight: 1,
};
const backLabelStyle = {
  whiteSpace: 'nowrap',
};
const topbarTitleStyle = {
  flex: 1,
  minWidth: 0,
  color: '#ffffff',
  fontSize: '0.95rem',
  fontWeight: 600,
  // Truncate long titles on narrow screens.
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'right',
};
const iframeStyle = {
  flex: 1,
  width: '100%',
  height: `calc(100vh - ${SITE_HEADER_OFFSET + TOPBAR_HEIGHT}px)`,
  border: 'none',
  background: '#0a1628',
  display: 'block',
};
const loadingWrapStyle = {
  minHeight: `calc(100vh - ${SITE_HEADER_OFFSET}px)`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
};
const loadingTextStyle = {
  color: '#a8c5e6',
  fontSize: '1rem',
};
